import { CheckCircleFilled, PlusOutlined, RightOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Alert, Button, Input, message, Modal, Popconfirm } from 'antd';
import { useRefreshIsOnlyOneAssistant } from 'hooks/business/isOnlyOneAssistant';
import { useOrgValue } from 'hooks/business/system/useOrg';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import useCrud from 'hooks/useCrud';
import useModal from 'hooks/useModal';
import { get, omit } from 'lodash-es';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { queryConfigByKey } from 'services/api/admin';
import {
  checkRolesConflict,
  update as updateAccount,
  updatePassword,
  updateStatus,
} from 'services/api/assistant.account';
import { hidePayment } from 'services/chattt';
import { errorToasterWithServerMsg } from 'utils/errorToaster';
import serverMsg from 'utils/serverMsg';
// import retryify from 'utils/retryify';
import seatSvg from './assets/seat.svg';
import { AccountFormModal, AccountPasswordFormModal, AccountTable, Confirm } from './components';
import { useAccountV2 } from './hooks/useAccount';
import useSeat from './hooks/useSeat';
import { accountOrderByStatus, parseDepartmentRolesField } from './utils';
import { T, useTranslate } from '@tolgee/react';
import { useMemoizedFn } from 'ahooks';
import { isMasterOnline, isSiteGiant } from 'productLine';
import BatchUpdateDrawer from './components/BatchUpdateDrawer';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 32px - var(--header-height) - var(--tabs-height));
  margin: 16px;
  border-radius: 2px;
  background: var(--fett-bg-panel);
  > header {
    flex: 0 0 60px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;
    border-bottom: solid 1px var(--fett-border-default);
    > h2 {
      margin-bottom: 0;
      font-size: 16px;
      [data-color-mode='dark'] & {
        color: var(--fett-text-primary);
      }
    }
  }
  > .${({ theme }) => theme.getAntdPrefixedClassName('alert')} {
    flex: 0 0 40px;
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    padding: 0 24px;
    overflow: hidden;
    .ant-pagination li:first-of-type {
      flex: 1 0 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
`;

export const useIsTrail = () => {
  const { activeExpireTimestamp = '0', tryExpireTimestamp = '0' } = useOrgValue() || {};

  const isTrail =
    parseInt(tryExpireTimestamp, 10) > Date.now() &&
    parseInt(activeExpireTimestamp, 10) < Date.now();

  return isTrail;
};

function Account() {
  const { t } = useTranslate();

  const nav = useNavigate();

  const isTrail = useIsTrail();

  const [keyword, setKeyword] = useState<string>();
  const { page, loading, list, fetchList, create, update, del } = useAccountV2();

  const { item: seat, fetch: fetchSeat } = useSeat();

  const { item: nonReceptionProcessMonthlyLimit, fetch: fetchNonReceptionProcessMonthlyLimit } =
    useCrud({
      read: () => queryConfigByKey('NON_RECEPTION_PROCESS_MONTHLY_LIMIT'),
    });

  const {
    isExpire = false,
    seatLimit = 0,
    enableSeat = 0,
    nonReceptionAccountNum = 0,
    enabledNonReceptionAccountNum = 0,
  } = seat || {};

  const supportNonReceptionAccount = nonReceptionAccountNum > 0;

  const refreshIsOnlyAssistant = useRefreshIsOnlyOneAssistant();

  const isLimit = Boolean(
    !isTrail && enableSeat >= seatLimit && enabledNonReceptionAccountNum >= nonReceptionAccountNum,
  );

  const { current = 1, pageSize = 10 } = page;
  const location = useLocation();
  const {
    visible: accountFormVisible,
    show: showAccountForm,
    hide: hideAccountForm,
    meta: AccountFormMeta,
  } = useModal<StoreHomeV2.Account>(!!(location.state as any)?.showAccountForm);

  const {
    visible: accountPasswordFormVisible,
    show: showAccountPasswordForm,
    hide: hideAccountPasswordForm,
    meta: accountPasswordFormMeta,
  } = useModal<StoreHomeV2.Account>();

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const batchUpdateDrawerState = useModal();

  const dataSource = useMemo(() => accountOrderByStatus(list, 'status'), [list]);
  const refs = useRef({
    fetchList,
    fetchSeat,
    t,
    refreshIsOnlyAssistant,
    fetchNonReceptionProcessMonthlyLimit,
  });
  refs.current = {
    fetchList,
    fetchSeat,
    t,
    refreshIsOnlyAssistant,
    fetchNonReceptionProcessMonthlyLimit,
  };

  const searchAccount = useMemoizedFn((value) => {
    setKeyword(value);
    fetchList({
      page: 1,
      pageSize: 10,
      keyword: value,
    }).catch(() => {
      message.error(t('account.fetchFailed'));
    });
  });

  useEffect(() => {
    const {
      current: { fetchList, fetchSeat, t },
    } = refs;

    fetchList({
      page: 1,
      pageSize: 10,
    }).catch(() => {
      message.error(t('account.fetchFailed'));
    });

    fetchSeat().catch(() => {});
  }, []);

  useEffect(() => {
    if (supportNonReceptionAccount) {
      refs.current.fetchNonReceptionProcessMonthlyLimit().catch(() => {});
    }
  }, [supportNonReceptionAccount]);

  const getPermission = useGetPermission();

  const onRefresh = () => {
    const { current = 1, pageSize = 10 } = page;
    fetchList({
      page: current,
      pageSize,
      keyword,
    }).catch(() => {
      message.error(t('account.fetchFailed'));
    });
  };

  return (
    <Container>
      <header>
        <h2>{t('nav.account')}</h2>
        {(() => {
          if (!getPermission('帐号管理')) {
            return null;
          }

          if (isExpire || isLimit) {
            return isMasterOnline ? null : (
              <Popconfirm
                placement="bottomRight"
                title={
                  <Confirm>
                    {/* eslint-disable-next-line no-nested-ternary */}
                    {isExpire
                      ? isTrail
                        ? t('account.当前试用已到期，请先订购服务套餐')
                        : t('account.当前订购已到期，请先订购服务套餐')
                      : t('account.当前坐席已达上限，请先订购客服坐席')}
                  </Confirm>
                }
                okText={isSiteGiant ? null : t('account.去订购')}
                onConfirm={() => !isSiteGiant && nav('/management/service/market')}
              >
                <Button type="primary" icon={<PlusOutlined />}>
                  {t('department.新建帐号')}
                </Button>
              </Popconfirm>
            );
          }

          return isMasterOnline ? null : (
            <Button icon={<PlusOutlined />} onClick={() => showAccountForm()}>
              {t('department.新建帐号')}
            </Button>
          );
        })()}
      </header>
      {isExpire && (
        <Alert
          banner
          showIcon
          type="warning"
          message={
            <div>
              <span>
                {isTrail
                  ? t('account.试用到期，请先订购服务套餐')
                  : t('account.订购到期，请先订购服务套餐')}
              </span>
              {!hidePayment && !isMasterOnline && !isSiteGiant && (
                <Button type="link" onClick={() => nav('/management/service/market')}>
                  {t('account.去订购')}
                  <RightOutlined />
                </Button>
              )}
            </div>
          }
        />
      )}
      <section>
        <div className="flex items-center h-[52px] justify-between">
          {seatLimit ? (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1 flex-nowrap whitespace-nowrap">
                <img className="w-4 h-4" src={seatSvg} alt="seat" />
                {supportNonReceptionAccount ? (
                  <T
                    keyName="account.不限额帐号x位限额帐号y位"
                    params={{ num: <span />, x: seatLimit, y: nonReceptionAccountNum }}
                  />
                ) : (
                  <T keyName="account.总坐席n位" params={{ num: <span />, n: seatLimit }} />
                )}
              </div>
              <div className="flex items-center gap-1 flex-nowrap whitespace-nowrap">
                <CheckCircleFilled className="!text-[var(--fett-scenario-success-text)]" />
                {supportNonReceptionAccount ? (
                  <T
                    keyName="account.启用不限额帐号x位限额帐号y位"
                    params={{ num: <span />, x: enableSeat, y: enabledNonReceptionAccountNum }}
                  />
                ) : (
                  <T keyName="account.启用n位" params={{ num: <span />, n: enableSeat }} />
                )}
              </div>
            </div>
          ) : (
            <div />
          )}
          <Input.Search
            className="!w-44"
            placeholder={t('请输入帐号搜索')}
            allowClear
            onSearch={searchAccount}
          />
        </div>

        <div className="flex-grow flex-shrink-0 basis-0 overflow-hidden">
          <AccountTable
            loading={loading}
            dataSource={dataSource}
            isExpire={isExpire}
            isLimit={isLimit}
            isTrail={isTrail}
            supportNonReceptionAccount={supportNonReceptionAccount}
            nonReceptionProcessMonthlyLimit={nonReceptionProcessMonthlyLimit}
            selectedIds={selectedIds}
            onSelectedIdsChange={setSelectedIds}
            pagination={useMemo(() => {
              return {
                ...page,
                showQuickJumper: true,
                showSizeChanger: true,
                showTotal: (x) => (
                  <>
                    {selectedIds.length > 0 ? (
                      <div>
                        {t('已选x个账号', { count: selectedIds.length })}
                        <Button type="link" onClick={batchUpdateDrawerState.show}>
                          {t('批量编辑')}
                        </Button>
                      </div>
                    ) : (
                      <div />
                    )}
                    <span>{t('feedback.共x项', { x })}</span>
                  </>
                ),
              };
            }, [page, t, batchUpdateDrawerState.show, selectedIds])}
            onChange={useCallback(
              (pagination) => {
                const { current = 1, pageSize = 10 } = pagination;

                fetchList({
                  page: current,
                  pageSize,
                  keyword,
                }).catch(() => {
                  message.error(t('account.fetchFailed'));
                });
              },
              [keyword, fetchList, t],
            )}
            onEditNick={showAccountForm}
            onEditPassword={showAccountPasswordForm}
            onUpdateStatus={useCallback(
              (account) => {
                const { id, status } = account;
                // id转型 兼容v1接口
                const submitValues = {
                  id: `${id}`,
                  status,
                };

                return update(() => updateStatus(submitValues), false, true, undefined, {
                  page: current,
                  pageSize,
                })
                  .then(() => {
                    message.success(t('account.updateStatusSucceed'));

                    const {
                      current: { fetchSeat, refreshIsOnlyAssistant },
                    } = refs;

                    fetchSeat().catch(() => {});
                    refreshIsOnlyAssistant();
                  })
                  .catch((err) => {
                    if (get(err, 'response.data.code') === 1057) {
                      message.error(t('errors.1057'));

                      return;
                    }

                    serverMsg(err)
                      .then(message.error)
                      .catch(() => {
                        message.error(t('account.updateStatusFailed'));
                      });
                  });
              },
              [update, current, pageSize, t],
            )}
            onDelete={useCallback(
              (account) => {
                // 兼容v1接口
                return del(`${account.id}`, true, {
                  page: current,
                  pageSize,
                })
                  .then(() => {
                    message.success(t('account.deleteSucceed'));

                    const {
                      current: { fetchSeat },
                    } = refs;

                    fetchSeat().catch(() => {});
                  })
                  .catch((err) => {
                    serverMsg(err)
                      .then(message.error)
                      .catch(() => {
                        message.error(t('account.deleteFailed'));
                      });
                  });
              },
              [del, current, pageSize, t],
            )}
          />
        </div>
      </section>
      <AccountFormModal
        showIncomingLineLimit
        type="drawer"
        onRefresh={onRefresh}
        visible={accountFormVisible}
        initialValues={AccountFormMeta}
        title={AccountFormMeta !== undefined ? t('department.编辑帐号') : t('department.新建帐号')}
        okText={
          AccountFormMeta !== undefined ? t('api.common.action.save') : t('department.确认新建')
        }
        seat={seat}
        nonReceptionProcessMonthlyLimit={nonReceptionProcessMonthlyLimit}
        isTrail={isTrail}
        onCancel={useCallback(() => hideAccountForm(), [hideAccountForm])}
        onSubmit={useCallback(
          async (values, helpers) => {
            const {
              id,
              name,
              phoneNumber,
              email: orgEmail,
              roleInDepartments,
              type,
              timeZone,
              incomingLineLimit,
            } = values;

            const email = orgEmail && orgEmail.trim() ? orgEmail : '';

            let action;

            const refreshParams = {
              page: current,
              pageSize,
            };

            const isEdit = id !== undefined;
            const newRoleIds = parseDepartmentRolesField(roleInDepartments);
            const { isConflict, baseRole, conflictRoles } = await checkRolesConflict({
              allRoleIds: newRoleIds,
            });
            if (isConflict) {
              const [roleA, roleB] = baseRole ? [baseRole, ...conflictRoles] : conflictRoles;
              if (roleA && roleB) {
                Modal.error({
                  title: t('department.无法保存'),
                  content: t('department.该帐号已选[x]和[y]权限冲突', {
                    x: `${roleA.departmentName}-${roleA.roleName}`,
                    y: `${roleB.departmentName}-${roleB.roleName}`,
                  }),
                });
              }
              return Promise.reject();
            }
            if (id !== undefined) {
              action = update(
                () =>
                  updateAccount({
                    // 兼容 v1接口
                    id: `${id}`,
                    name,
                    phoneNumber,
                    email,
                    newRoleIds,
                    type,
                    timeZone,
                    incomingLineLimit,
                  }),
                false,
                true,
                undefined,
                refreshParams,
              );
            } else {
              const { account, confirmPassword: _confirmPassword, ...skipAccount } = values;
              const user = { ...skipAccount, email } as any;
              if (account) {
                user.account = account;
              }
              action = create(
                {
                  user: omit(user, ['roleInDepartments']) as any,
                  newRoleIds,
                },
                true,
                refreshParams,
              );
            }
            return action
              .then(() => {
                hideAccountForm();
                helpers.resetForm();
                if (isEdit && type !== AccountFormMeta?.type) {
                  message.success(t('account.typeChangeTips'));
                } else {
                  message.success(isEdit ? t('account.updateSucceed') : t('account.createSucceed'));
                }
                const {
                  current: { fetchSeat, refreshIsOnlyAssistant },
                } = refs;
                fetchSeat().catch(() => {});
                if (!isEdit) {
                  refreshIsOnlyAssistant();
                }
              })
              .catch((err) => {
                errorToasterWithServerMsg(err, t('translation.操作失败，请稍后再试'));
              });
          },
          [create, update, hideAccountForm, current, pageSize, AccountFormMeta, t],
        )}
      />
      <AccountPasswordFormModal
        initialValues={useMemo(() => {
          if (accountPasswordFormMeta === undefined) {
            return accountPasswordFormMeta;
          }

          return {
            ...omit(accountPasswordFormMeta, 'id'),
            id: `${accountPasswordFormMeta?.id}`,
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
          };
        }, [accountPasswordFormMeta])}
        visible={accountPasswordFormVisible}
        onCancel={useCallback(() => hideAccountPasswordForm(), [hideAccountPasswordForm])}
        onSubmit={useCallback(
          (values, helpers) => {
            const { id, oldPassword, newPassword } = values;

            const submitValues = {
              id,
              oldPasswd: oldPassword || undefined,
              newPasswd: newPassword,
            };

            return update(() => updatePassword(submitValues), false, true, undefined, {
              page: current,
              pageSize,
            })
              .then(() => {
                hideAccountPasswordForm();

                helpers.resetForm();

                message.success(t('account.updatePasswordSucceed'));
              })
              .catch((err) => {
                serverMsg(err)
                  .then(message.error)
                  .catch(() => {
                    message.error(t('account.updatePasswordFailed'));
                  });
              });
          },
          [update, hideAccountPasswordForm, current, pageSize, t],
        )}
      />
      <BatchUpdateDrawer
        userIds={selectedIds}
        visible={batchUpdateDrawerState.visible}
        onClose={batchUpdateDrawerState.hide}
        onUpdate={onRefresh}
      />
    </Container>
  );
}

export default memo(Account);
